import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, filter, first, map, startWith, switchMap } from 'rxjs/operators';

import { AccountFacade } from '@mode/accounts/contract';
import { ChartFacade } from '@mode/charts/contract';
import { MetricDomain } from '@mode/shared/contract-common';
import { generateUUID, isPresent, shareReplayDefault } from '@mode/shared/util-js';

import { MetricsHelpers } from '..';
import * as DimensionActions from './dimension/dimension.actions';
import * as DimensionSelectors from './dimension/dimension.selectors';
import { DomainAppState } from './metric-domain.types';
import * as MetricActions from './metric/metric.actions';
import * as MetricSelectors from './metric/metric.selectors';

@Injectable()
export class MetricFacade {
  constructor(
    private store: Store<DomainAppState>,
    private accountFacade: AccountFacade,
    private chartFacade: ChartFacade
  ) {
    this.isMetricsComplete$ = store.select(MetricSelectors.selectIsMetricsComplete);
    this.isMetricsLoading$ = store.select(MetricSelectors.selectIsMetricsLoading);
    this.metricsError$ = store.select(MetricSelectors.metricsError);
    this.metrics$ = combineLatest([store.select(MetricSelectors.selectMetrics), this.isMetricsComplete$]).pipe(
      filter(([, complete]) => complete),
      map(([metrics]) => metrics)
    );
    this.isMetricRunComplete$ = store.select(MetricSelectors.selectIsMetricsRunComplete);

    this.selectedMetricToken$ = store.select(MetricSelectors.selectMetricToken);
    this.selectedMetricVizType$ = store.select(MetricSelectors.selectMetricVizType);
    this.selectedQueryRawSql$ = store.select(MetricSelectors.selectSelectedQueryRawSql);
    this.selectedMetricName$ = this.selectedMetricToken$.pipe(
      filter(isPresent),
      switchMap((metricToken) => this.createMetricNameFromToken(metricToken))
    );

    this.selectedMetric$ = combineLatest([this.selectedMetricToken$, this.metrics$]).pipe(
      filter(([token, metrics]) => metrics.some((metric) => metric.token === token)),
      map(([token, metrics]) => metrics.find((m) => m.token === token)),
      distinctUntilChanged((a, b) => a?.token === b?.token)
    );

    this.newMetricQueryData$ = combineLatest([
      // this is needed because when you create a new SQL report there's no metric selected so we emit undefined
      this.selectedMetric$.pipe(startWith(undefined as unknown as MetricDomain.Metric)),
      this.selectedMetricVizType$,
      this.accountFacade.defaultCategoricalPalette$,
      this.accountFacade.defaultCategoricalChartFormat$,
    ]).pipe(
      map(([metric, vizType, defaultPalette, defaultChartFormat]) => {
        if (metric && vizType) {
          const chart = MetricsHelpers.createChart(metric, vizType, defaultPalette.token, defaultChartFormat);
          const dbtSql = MetricsHelpers.createDbtSqlFromSpec(MetricsHelpers.createMacroSpecFromMetric(metric, vizType));
          return {
            metricId: metric.id,
            metricToken: metric.token,
            dataSourceId: metric.dataSourceId,
            vizType,
            chart,
            dbtSql,
            mappingId: generateUUID(),
          };
        }
      }),
      shareReplayDefault()
    );

    this.isDimensionsComplete$ = store.select(DimensionSelectors.selectIsDimensionsComplete);
    this.isDimensionsLoading$ = store.select(DimensionSelectors.selectIsDimensionsLoading);
    this.dimensions$ = combineLatest([
      store.select(DimensionSelectors.selectDimensions),
      this.isDimensionsComplete$,
    ]).pipe(
      filter(([, complete]) => complete),
      map(([dimensions]) => dimensions)
    );
  }

  public metrics$: Observable<ReadonlyArray<MetricDomain.Metric>>;
  public isMetricsComplete$: Observable<boolean>;
  public isMetricsLoading$: Observable<boolean>;
  public metricsError$: Observable<Error | null>;
  public isMetricRunComplete$: Observable<boolean>;

  public selectedMetricToken$: Observable<string | null>;
  public selectedMetricVizType$: Observable<MetricDomain.VizTypeSelection | null>;
  public selectedMetricName$: Observable<string | undefined>;
  public selectedMetric$: Observable<MetricDomain.Metric | undefined>;
  public newMetricQueryData$: Observable<MetricDomain.NewMetricQueryData | undefined>;
  public selectedQueryRawSql$: Observable<string | null>;

  public dimensions$: Observable<ReadonlyArray<MetricDomain.Dimension>>;
  public isDimensionsComplete$: Observable<boolean>;
  public isDimensionsLoading$: Observable<boolean>;

  public createMetricNameFromToken = (metricToken: string) =>
    this.store.select(MetricSelectors.createMetricNameSelector(metricToken));

  public createMetricChartState = (vizToken: string) =>
    this.store.select(MetricSelectors.createMetricChartStateSelector(vizToken));

  /**
   * Loads all the metrics, public and private. This will use the
   * current org in the account facade to retrieve the private metrics. If the
   * current org is public (a user), then it will only retrieve the public warehouse.
   *
   * TODO: Fix action hygiene
   */
  public metricDialogLoadMetrics() {
    this.store.dispatch(MetricActions.metricDialogLoadMetrics());
  }

  public metricRouteLoadDimensions(metricToken: string) {
    this.store.dispatch(DimensionActions.metricRouteLoadDimensions({ metricToken }));
  }

  public metricRouteLoadMetric(metricToken: string) {
    this.store.dispatch(MetricActions.metricQueryRouteLoadMetric({ metricToken }));
  }

  public metricRouteSelectMetric(metricToken: string) {
    this.store.dispatch(MetricActions.metricQueryRouteSelectMetric({ metricToken }));
  }

  public metricRouteSelectVizType(vizType: MetricDomain.VizTypeSelection) {
    this.store.dispatch(MetricActions.metricQueryRouteSelectVizType({ vizType }));
  }

  public createMetric(metricToken: string, vizType: MetricDomain.VizTypeSelection) {
    this.store.dispatch(
      MetricActions.createMetricQuery({
        metricToken,
        vizType,
      })
    );
  }

  public metricQueryRunCompleted(metricToken: string, queryToken: string) {
    this.store.dispatch(
      MetricActions.metricsRunComplete({
        metricToken,
        queryToken,
      })
    );
  }

  public setSelectedQueryRawSql(rawSql: string | null) {
    this.store.dispatch(
      MetricActions.setSelectedQueryRawSql({
        rawSql,
      })
    );
  }

  public selectChart(token: string) {
    // Chart selection
    this.chartFacade.selectChart(token);
    // Mark ephemeral so we don't persist chart changes via. the API
    this.chartFacade.setEphemeral(token);
    // Commit initial state
    this.store.dispatch(
      MetricActions.initChartStateIfUnset({
        state: 'committed',
        token,
      })
    );
  }

  public commitChart(token: string) {
    this.selectedMetric$.pipe(filter(isPresent), first()).subscribe((metric) => {
      // Update chart resource
      this.chartFacade.updateChartFromSnapshot(token, MetricsHelpers.createModel(metric));
      // Fetch data from flamingo
      this.chartFacade.manuallyApplyChanges(token);
    });
  }
}
